import * as React from "react";
import {cx}       from "emotion";

import "./gallery-image.scss";

import {ComponentLink,
        Link,
        makeChain}         from "@Common/react/cst2";
import {Types}             from "@Common/types";
import {ConfirmDialog}     from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {Tooltip2}          from "@Common/ui-components/heavy/common/design2/tooltip-2/tooltip-2";
import {bytesToUnitString} from "@Common/utils/math/bytes-to-unit-string";

import {MoreMenu}        from "./more-menu";
import {GetImageQuality} from "../utils/get-image-quality";
import {IsMobile}        from "../utils/is-mobile";


export const GalleryImage = ({
    id,
    thumbnailUrl,
    viewType,
    imageName             = "",
    fileSize              = 0, // image file size in bytes
    fileType              = "",
    imageAttributionData  = null,
    width                 = 0, // image width in pixels
    height                = 0, // image height in pixels
    areaWidth             = 0, // image target area width in inches
    areaHeight            = 0, // image target area height in inches
    photoQualitySettings  = null, // breakpoints for determining quality when the target area dimensions are not known
    allowLowQuality       = true,
    warnMediumQuality     = false, // if true then an alert is provided when using a medium-quality image
    isDesignationImage    = false,
    disabled              = false,
    selectedIndex         = 0, // if image is selected, will hold the selection sequence number
    lazyLoad              = false,
    onClick               = null,
    onEdit                = null,
    onDelete              = null,
    onDragStart           = null,
    onThumbnailLoadError  = null,
}:{
    id                    : number | string, // will be string only when displaying a photo service image
    thumbnailUrl          : string,
    viewType              : Types.Common.UiComponents.PhotoDashboard.viewTypes,
    imageName            ?: string,
    fileSize             ?: number,
    fileType             ?: string,
    imageAttributionData ?: Types.Apis.Models.GalleryImageAttribution,
    width                ?: number,
    height               ?: number,
    areaWidth            ?: number,
    areaHeight           ?: number,
    photoQualitySettings ?: Types.Common.UiComponents.PhotoDashboard.Models.PhotoQualitySettings,
    allowLowQuality      ?: boolean,
    warnMediumQuality    ?: boolean,
    isLogoImage          ?: boolean,
    isDesignationImage   ?: boolean,
    disabled             ?: boolean,
    selectedIndex        ?: number,
    lazyLoad             ?: boolean,
    onClick              ?: () => void,
    onEdit               ?: () => void,
    onDelete             ?: () => void,
    onDragStart          ?: (event : React.DragEvent<HTMLImageElement>) => void,
    onThumbnailLoadError ?: (currentTarget : React.SyntheticEvent<HTMLImageElement>) => void,
}) => (

    makeChain<JSX.Element>()

    // check for mobile
    .then("isMobile", (_, next:Link<JSX.Element, boolean>) => IsMobile({next}))

    // build image size information line
    .then("imageSizeInfo", (_, next:Link<JSX.Element, string[]>) => {

        let infoSections = [];

        if (fileSize > 0)
            infoSections.push(bytesToUnitString(fileSize, 1));
        if (width > 0 && height > 0)
            infoSections.push(width + " x " + height + "px");

        return next(infoSections);

    })

    // find image quality
    .then("imageQuality", (_, next:Link<JSX.Element, Types.Common.UiComponents.PhotoDashboard.Models.ImageQuality>) => next(
        GetImageQuality({
            imageInfo: {width, height, fileSize, fileType},
            areaInfo: {
                width: areaWidth,
                height: areaHeight,
            },
            photoQualitySettings,
        })
    ))

    // disable the image if the image is low quality and low quality images are not allowed - removing the option
    // altogether would cause pagination to show fewer results per page than expected
    .then(({imageQuality}, next:Link<JSX.Element>) => {

        if (!allowLowQuality && imageQuality.isLowQuality) {
            disabled = true;
        }

        return next();

    })

    // create the image dpi tooltip if dpi is available
    .then("tooltip", ({imageQuality}, next:Link<JSX.Element>) => (imageQuality.dpi > 0) ? Tooltip2({
        getMessage: () => `Image prints at ${Math.round(imageQuality.dpi)}dpi`,
        direction: "south-west",
        next,
    }) : next(null))

    .then("dialogConfirmLowQuality", (_, next:ComponentLink<typeof ConfirmDialog>) => ConfirmDialog({next}))

    // render the image area
    .then(({isMobile, imageSizeInfo, imageQuality, tooltip, dialogConfirmLowQuality}) => (
        <div className={cx("gallery-image-96a card", "viewtype-"+viewType, {disabled, selected: !!selectedIndex})} xd-qa-type="card" xd-qa-name={imageName + " Card"}>
            <a xd-qa-type="link" xd-qa-name={"Image Link"}
                onClick={async () => {

                    // require confirmation of low quality image use
                    if (imageQuality.isLowQuality && !isDesignationImage
                        && !await dialogConfirmLowQuality.show({
                            message: "Low Quality Warning",
                            actionContent: "This is a low-quality image that may print poorly. No credits will be issued for using a low quality image.",
                            buttons: {
                                confirmLabel: "Use Image Anyway",
                                cancelLabel: "Cancel"
                            },
                        })
                    ) return;
                    // require confirmation of medium quality image use
                    if (imageQuality.isMediumQuality && warnMediumQuality && !isDesignationImage
                        && !await dialogConfirmLowQuality.show({
                            message: "Medium Quality Warning",
                            actionContent: "The image you have selected is of MEDIUM QUALITY, and may result in less-than-optimal print quality if used. For the best print quality, please upload and use an image of HIGH QUALITY. NO CREDITS OR REFUNDS will be provided for using images of less than HIGH QUALITY.",
                            buttons: {
                                confirmLabel: "Use Image Anyway",
                                cancelLabel: "Cancel"
                            },
                        })
                    ) return;

                    onClick();

                }}
            >
                {!!selectedIndex && (
                <div className="selection-sequence">{selectedIndex}</div>
                )}
                <img xd-qa-type="image" xd-qa-name={"Image"}
                    className="thumbnail"
                    src={thumbnailUrl}
                    onError={onThumbnailLoadError}
                    onDragStart={onDragStart}
                    draggable={!!onDragStart}
                    loading={lazyLoad ? "lazy" : "eager"}
                    /* TODO: Add loading spinner as default until image loads
                    src="assets/SPINNER_NO_TEXT.svg"
                    */
                />
            </a>
            {(imageName || width > 0 || height > 0 || onEdit || onDelete) && (
            <div className="description">
                {!isDesignationImage && imageQuality.label && (
                    <div className="quality-controls">
                        <div className={cx("badge", imageQuality.label.toLowerCase())}
                            xd-qa-type="badge" xd-qa-name="Image Quality"
                            onMouseEnter={tooltip?.onMouseEnter}
                            onMouseLeave={tooltip?.onMouseLeave}
                            onClick={tooltip?.onClick}
                        >
                            {imageQuality.label}
                            {tooltip && (
                                <div
                                    className="tooltip"
                                    ref={tooltip.ref}
                                    xd-qa-type="text" xd-qa-name="Tooltip"
                                >
                                    {tooltip.render()}
                                </div>
                            )}
                        </div>
                        {viewType === "condensed" && isMobile && ( onEdit || onDelete ) && (
                            <MoreMenu
                                items={[onEdit && {
                                    label: "Edit",
                                    iconClass: "fa-pencil",
                                    onClick: onEdit,
                                },
                                onDelete && {
                                    label: "Delete",
                                    iconClass: "fa-trash",
                                    onClick: onDelete,
                                }].filter(Boolean)}
                            />
                        )}
                    </div>
                )}
                {imageName && (
                    <h3 xd-qa-type="label" title={imageName} xd-qa-name="Image Name">{imageName}</h3>
                )}
                {imageAttributionData && (
                    <h4 xd-qa-type="label" xd-qa-name="Image Attribution">
                        Photo by{" "}
                        <a href={imageAttributionData.userUrl} target="_new">{imageAttributionData.userName}</a>{" "}
                        on{" "}
                        <a href={imageAttributionData.platformUrl} target="_new">{imageAttributionData.platformName}</a>
                    </h4>
                )}
                {imageSizeInfo && (
                    <h5>
                        {imageSizeInfo.map((info, index) => <React.Fragment key={"imageInfo"+index}>
                            <span xd-qa-type="text" xd-qa-name={"File Size " + index}>{info}</span>
                            {index < imageSizeInfo.length-1 && (
                            <span className="separator">•</span>
                            )}
                        </React.Fragment>)}
                    </h5>
                )}
                {( onEdit || onDelete ) && (viewType !== "condensed" || !isMobile) && (
                    <div className="edit-controls">
                    {onEdit && (
                        <button className="text"
                        xd-qa-type="button" xd-qa-name={"Edit"}
                        onClick={onEdit}>Edit</button>
                    )}
                    {onEdit && onDelete && <div className="separator"/>}
                    {onDelete && (
                        <button className="text muted"
                        xd-qa-type="button" xd-qa-name={"Delete"}
                        onClick={onDelete}>Delete</button>
                    )}
                </div>
                )}
            </div>
            )}
        </div>
    ))

    .run()

);
